/**
 * Staff timelog labor cost/bill — aligned with JobCostMemberClockInTimer.
 * Prefers payout/timelog persisted values (amount, total_hourly_rate, billable_amount)
 * so explicit 0 overrides are not recomputed from default rates.
 */

/** $/hr from labour_cost_services catalog: T&M uses total_cost (with markup), else cost (originalCost). */
export function getLabourCatalogUnitCost(service, isTM = false) {
  if (!service) return 0;
  const base = Number(service.originalCost ?? service.original_cost ?? service.cost ?? 0);
  // OLD: const withMarkup = Number(service.labour_service_total_cost ?? service.service_original_cost ?? 0);
  const withMarkup = Number(
    service.labour_service_total_cost ??
      service.service_original_cost ??
      service.total_cost ??
      0
  );
  if (isTM && withMarkup > 0) return withMarkup;
  if (base > 0) return base;
  return 0;
}

/** $/hr for a project_subtasks row (workflow_stage_id → labour_cost_services). */
export function resolveSubtaskWorkflowLaborRate(subtaskRow, isTM = false) {
  if (!subtaskRow) return 0;
  const stageId = Number(subtaskRow.workflow_stage_id ?? 0);
  const serviceId = Number(subtaskRow.service_id ?? 0);
  if (stageId <= 0 && serviceId <= 0) return 0;
  return getLabourCostServiceCatalogRate(
    {
      cost: subtaskRow.workflow_service_cost,
      total_cost: subtaskRow.workflow_service_total_cost
    },
    isTM
  );
}

/** $/hr from user row when subtask has workflow_stage_id → labour_cost_services via API. */
export function resolveWorkflowStageCatalogRate(userRow, isTM = false) {
  const stageId = Number(userRow?.workflow_stage_id ?? 0);
  const serviceId = Number(userRow?.service_id ?? 0);
  if (stageId <= 0 && serviceId <= 0) return 0;
  return getLabourCostServiceCatalogRate(
    {
      cost: userRow?.workflow_service_cost,
      total_cost: userRow?.workflow_service_total_cost
    },
    isTM
  );
}

/** labour_cost_services row from job-cost service picker (cost = base, total_cost = with markup). */
export function getLabourCostServiceCatalogRate(lcsRow, isTM = false) {
  if (!lcsRow) return 0;
  return getLabourCatalogUnitCost(
    {
      cost: lcsRow.cost,
      originalCost: lcsRow.cost,
      original_cost: lcsRow.cost,
      labour_service_total_cost: lcsRow.total_cost,
      total_cost: lcsRow.total_cost
    },
    isTM
  );
}

/** Stored rate vs catalog: T&M upgrades base→markup; non-T&M downgrades markup→base. */
export function normalizeStoredLaborRate(service, isTM, storedRate) {
  const r = Number(storedRate);
  if (!Number.isFinite(r) || r <= 0) return 0;
  const base = Number(service.originalCost ?? service.original_cost ?? service.cost ?? 0);
  const markup = Number(service.labour_service_total_cost ?? service.service_original_cost ?? 0);
  if (isTM && markup > 0 && base > 0 && Math.abs(r - base) < 0.01) return markup;
  if (!isTM && markup > 0 && base > 0 && Math.abs(r - markup) < 0.01) return base;
  return r;
}

/** Install Labor service line unit cost for job-cost labor (T&M prefers markup catalog rate). */
export function resolveInstallLaborServiceUnitCost(service, isTM = false) {
  if (!service) return 0;
  const jc = Number(service.jobcost_unit_cost);
  const catalog = getLabourCatalogUnitCost(service, isTM);
  if (Number.isFinite(jc) && jc > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, jc);
    if (normalized > 0) return normalized;
  }
  if (catalog > 0) return catalog;
  const uc = Number(service.unit_cost);
  if (Number.isFinite(uc) && uc > 0) {
    const normalized = normalizeStoredLaborRate(service, isTM, uc);
    if (normalized > 0) return normalized;
  }
  const qty = Number(service.jobcost_quantity ?? service.quantity ?? service.qty ?? 0);
  const totalC = Number(service.jobcost_total_cost ?? 0);
  if (qty > 0 && totalC > 0) {
    return normalizeStoredLaborRate(service, isTM, totalC / qty);
  }
  return 0;
}

const roundMoney = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Install Labor line total — for T&M, rows saved before markup used jobcost_total_cost at base rate (cost × hours).
 * Recompute from markup catalog rate when saved total still matches that stale base calculation.
 */
export function resolveInstallLaborServiceLineCost(service, isTM = false, hoursOverride) {
  if (!service) return 0;
  const saved = Number(service.jobcost_total_cost ?? 0);
  const hours = Number(
    hoursOverride ?? service.jobcost_quantity ?? service.qty ?? service.quantity ?? 0
  );
  const markupRate = resolveInstallLaborServiceUnitCost(service, isTM);
  if (hours <= 0) return saved > 0 ? saved : 0;

  const markupTotal = roundMoney(hours * markupRate);
  // OLD: if (!isTM) return saved > 0 ? saved : markupTotal;
  if (!isTM) {
    const baseRate = getLabourCatalogUnitCost(service, false);
    if (hours > 0 && baseRate > 0) return roundMoney(hours * baseRate);
    return saved > 0 ? saved : markupTotal;
  }
  if (saved <= 0) return markupTotal;

  const baseRate = Number(service.originalCost ?? service.original_cost ?? service.cost ?? 0);
  const staleBaseTotal = baseRate > 0 ? roundMoney(hours * baseRate) : 0;
  if (staleBaseTotal > 0 && Math.abs(saved - staleBaseTotal) <= 0.05) {
    return markupTotal;
  }
  return saved;
}

/**
 * Request service line total for job-cost totals (Labor tab + recipeCost).
 * Non-T&M: hours × catalog rate without markup; T&M: markup + legacy stale-base fix for install labor.
 */
export function resolveJobCostServiceLineTotal(service, isTM = false, hoursOverride) {
  if (!service) return 0;
  if (Number(service.labour_cost_service_id) === 10) {
    return resolveInstallLaborServiceLineCost(service, isTM, hoursOverride);
  }
  const hours = Number(
    hoursOverride ?? service.jobcost_quantity ?? service.qty ?? service.quantity ?? 0
  );
  const rate = resolveInstallLaborServiceUnitCost(service, isTM);
  const saved = Number(service.jobcost_total_cost ?? 0);
  // OLD: return parseFloat(service.jobcost_total_cost || 0) — could be markup total on cost-card jobs
  if (hours > 0 && rate > 0) return roundMoney(hours * rate);
  return saved > 0 ? saved : 0;
}

/** Request_services row for Install Labor (labour_cost_service_id 10), if present on the job. */
export function findInstallLaborServiceRecord(servicesList, user) {
  const list = servicesList || [];
  const fromList = list.find((s) => Number(s.labour_cost_service_id) === 10);
  if (fromList) return fromList;
  const sid = Number(user?.service_id || user?.rs_id || 0);
  if (sid > 0) {
    const byTimelogService = list.find(
      (s) => Number(s.id) === sid && Number(s.labour_cost_service_id) === 10
    );
    if (byTimelogService) return byTimelogService;
  }
  return null;
}

/** Member/installer row should use Install Labor catalog $/hr (not staff hourly). */
// OLD: only (isInstaller || isDriver) with fallback when linked service had no cost
export function shouldUseInstallLaborCatalogRate(entry) {
  const isInstaller =
    entry?.installation === true ||
    entry?.installation === 1 ||
    entry?.is_installer === 1 ||
    entry?.is_installer === 1;
  const isDriver =
    entry?.isDriver === 1 ||
    entry?.isDriver === '1' ||
    entry?.is_driver === 1 ||
    entry?.is_driver === '1';
  const taskName = (entry?.task_name || '').toString().trim().toLowerCase();
  const roleName = (entry?.role_name || '').toString().trim().toLowerCase();
  const isDriverMember =
    !entry?.task_name ||
    taskName.includes('driver') ||
    roleName.includes('driver') ||
    isDriver;
  return isInstaller || isDriverMember || isRecipeCostInstallLaborTimelog(entry);
}

// Backup before Install Labor request-service classification:
// Install Labor checks only compared the row service id directly to 10 or used text/driver/manual fallbacks.
const getEntryServiceId = (entry) =>
  Number(entry?.service_id || entry?.rs_id || entry?.customer_order_product_id || 0);

const hasInstallLaborRequestServiceId = (entry, servicesList = []) => {
  const sId = getEntryServiceId(entry);
  if (!sId) return false;
  return (servicesList || []).some((service) => {
    if (Number(service?.labour_cost_service_id) !== 10) return false;
    return [service?.id, service?.request_service_id, service?.service_id]
      .some((value) => Number(value) === sId);
  });
};

/** Timelog / payout row text identifies Install Labor. */
const isInstallLaborText = (value) => {
  const normalized = (value || '').toString().trim().toUpperCase();
  return normalized.includes('INSTALL LABOR') || normalized.includes('LABOR INSTALL');
};

/** Install-labor / job-cost rows: use request service line rate instead of staff hourly when fallback is passed (recipe cost). */
export function isRecipeCostInstallLaborTimelog(log, servicesList = []) {
  const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
  const isDriver =
    log?.isDriver === 1 ||
    log?.isDriver === '1' ||
    log?.is_driver === 1 ||
    log?.is_driver === '1';
  const isInstaller =
    log?.installation === true ||
    log?.installation === 1 ||
    log?.is_installer === 1 ||
    log?.is_installer === 1;
  const taskText = [
    log?.task_name,
    log?.service_name,
    log?.service_label,
    log?.description
  ].filter(Boolean).join(' ');
  const isManual = log?.is_manually_added === 1 || log?.is_manually_added === '1';
  // OLD: return sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver || !isManual));
  // Backup before Install Labor request-service classification:
  // const taskName = (log?.task_name || '').toString().trim().toUpperCase();
  // if (isInstaller || isDriver) return true;
  // return sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver || !isManual));
  if (isInstaller || isDriver) return true;
  if (hasInstallLaborRequestServiceId(log, servicesList)) return true;
  // OLD backup: (sId === 0 && !isManual) — member-added production timelogs on recipe tasks were misclassified as Install Labor.
  return (
    sId === 10 ||
    isInstallLaborText(taskText) ||
    (sId === 0 && !isManual && (!Number(log?.task_id) || isInstallLaborText(taskText)))
  );
}

/** Hours for job-cost totals — matches Labor tab (total_hours + minutes, rounded). */
export function resolveJobCostTimelogDecimalHours(log) {
  const totalHours = Number(log?.total_hours) || 0;
  const totalMinutes = Number(log?.total_minutes) || 0;
  const otHours = Number(log?.ot_hours ?? log?.total_ot_hours) || 0;
  const apiDecimalHours = Number(log?.hours) || 0;
  const computedHoursFromTotals = roundMoney(totalHours + totalMinutes / 60);
  const decimalTotalHours =
    computedHoursFromTotals > 0 ? computedHoursFromTotals : roundMoney(apiDecimalHours);
  const regularHours = roundMoney(Math.max(0, decimalTotalHours - otHours));
  return { decimalTotalHours, regularHours, otHours: roundMoney(otHours) };
}

/**
 * Job-cost line cost (Production/Install buckets) — catalog hours × rate, not staff payout.
 * OLD: recipeCost used getLaborTimelogCost which returned payouts.amount (~10.83) instead of Labor tab cost (~11.05).
 */
export function getJobCostTimelogLineCost(log, isTM = false, installServiceRate = 0) {
  const { decimalTotalHours, regularHours, otHours } = resolveJobCostTimelogDecimalHours(log);
  if (regularHours <= 0 && otHours <= 0 && decimalTotalHours <= 0) return 0;

  const workflowRate = resolveWorkflowStageCatalogRate(log, isTM);
  let rate = workflowRate > 0 ? workflowRate : 0;
  if (rate === 0) {
    const hr = Number(log?.hourly_rate);
    if (Number.isFinite(hr) && hr > 0) rate = hr;
  }
  const fb = Number(installServiceRate);
  if (rate === 0 && fb > 0 && isRecipeCostInstallLaborTimelog(log)) rate = fb;

  const isInstaller =
    log?.installation === true ||
    log?.installation === 1 ||
    log?.is_installer === 1;
  const isDriver =
    log?.isDriver === 1 ||
    log?.isDriver === '1' ||
    log?.is_driver === 1 ||
    log?.is_driver === '1';
  if ((isInstaller || isDriver) && !isTM) {
    return roundMoney(decimalTotalHours * rate);
  }
  return roundMoney(regularHours * rate + otHours * rate * 1.5);
}

// OLD: Other-tab filter lived inline in JobCostEquipmentsTab useEffect + recipeCost otherTabServicesCount.
// OLD (non-T&M): return Number(s.from_job) === 1; — Install Labor with from_job === 1 still appeared on Other tab.
// OLD (T&M table render): rows.filter(row => !(isTM && (serviceId === 10 || labour_cost_service_id === 10)))

/** request_services row is Install Labor (Labor tab only — never Job Cost "Other" tab). */
export function isJobCostInstallLaborService(service) {
  if (!service) return false;
  return (
    Number(service.id) === 10 || Number(service.labour_cost_service_id) === 10
  );
}

/**
 * Job Cost Expense → "Other" tab (JobCostEquipmentsTab):
 * T&M: all services except Install Labor (including sales order / estimate lines).
 * Non-T&M: only services added from job cost (from_job === 1), still excluding Install Labor.
 */
export function shouldShowServiceOnJobCostOtherTab(service, isTM = false) {
  if (!service) return false;
  if (isJobCostInstallLaborService(service)) return false;
  if (isTM) return true;
  return Number(service.from_job) === 1;
}

export function isJobCostInstallLaborPayout(payout, servicesList = []) {
  const sId = Number(payout?.service_id || payout?.rs_id || payout?.customer_order_product_id || 0);
  const taskName = [
    payout?.task_name,
    payout?.service_name,
    payout?.name,
    payout?.description
  ].filter(Boolean).join(' ');
  // Backup before T&M production labor fix: only service_id was available for payout classification.
  // Backup before Install Labor request-service classification:
  // const taskName = [
  //   payout?.task_name,
  //   payout?.name,
  //   payout?.description
  // ].filter(Boolean).join(' ');
  // return sId === 10 || isInstallLaborText(taskName);
  return (
    sId === 10 ||
    Number(payout?.labour_cost_service_id) === 10 ||
    hasInstallLaborRequestServiceId(payout, servicesList) ||
    isInstallLaborText(taskName)
  );
}

export function isJobCostInstallLaborTimelog(log, servicesList = []) {
  const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
  const isDriver =
    log?.isDriver === 1 ||
    log?.isDriver === '1' ||
    log?.is_driver === 1 ||
    log?.is_driver === '1';
  const isInstaller =
    log?.installation === true ||
    log?.installation === 1 ||
    log?.is_installer === 1 ||
    log?.is_installer === '1';
  const taskText = [
    log?.task_name,
    log?.service_name,
    log?.service_label,
    log?.description
  ].filter(Boolean).join(' ');
  const isManual = log?.is_manually_added === 1 || log?.is_manually_added === '1';
  // Backup before Install Labor request-service classification:
  // const taskName = (log?.task_name || '').toString().trim().toUpperCase();
  // return (
  //   sId === 10 ||
  //   (sId === 0 &&
  //     (
  //       // Backup before T&M production labor fix: taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL'
  //       isInstallLaborText(taskName) ||
  //       isDriver ||
  //       !isManual))
  // );
  return (
    sId === 10 ||
    Number(log?.labour_cost_service_id) === 10 ||
    hasInstallLaborRequestServiceId(log, servicesList) ||
    isInstallLaborText(taskText) ||
    isInstaller ||
    isDriver ||
    // OLD backup: (sId === 0 && !isManual) — member-added production timelogs on recipe tasks were excluded from Production Labor.
    (sId === 0 &&
      !isManual &&
      (!Number(log?.task_id) || isInstallLaborText(taskText)))
  );
}

/** Production timelog hours only (excludes Install Labor) — for Compare Actual qty/hrs column. */
export function sumJobCostProductionTimelogHours(logs, services) {
  let total = 0;
  (logs || []).forEach((log) => {
    if (isJobCostInstallLaborTimelog(log, services)) return;
    const { decimalTotalHours } = resolveJobCostTimelogDecimalHours(log);
    total += decimalTotalHours;
  });
  return roundMoney(total);
}

/** Production/subtask timelog labor (excludes Install Labor) — matches recipeCost Labor tab. */
export function sumJobCostProductionTimelogLabor(logs, services, isTM = false) {
  const installLaborService = (services || []).find((s) => Number(s.labour_cost_service_id) === 10);
  const installServiceRate = resolveInstallLaborServiceUnitCost(installLaborService, isTM);
  let total = 0;
  (logs || []).forEach((log) => {
    // Backup before Install Labor request-service classification:
    // if (isJobCostInstallLaborTimelog(log)) return;
    if (isJobCostInstallLaborTimelog(log, services)) return;
    total += getJobCostTimelogLineCost(log, isTM, installServiceRate);
  });
  return roundMoney(total);
}

export function getLaborTimelogCost(log, fallbackRate) {
  const payoutAmount = Number(log?.amount);
  if (Number.isFinite(payoutAmount)) return payoutAmount;

  const totalH = Number(log?.hours || log?.total_hours) || 0;
  const otH = Number(log?.ot_hours || log?.total_ot_hours) || 0;
  const storedTotal = Number(log?.total_hourly_rate);

  if (Number.isFinite(storedTotal)) {
    if (totalH === 0 && otH === 0 && storedTotal !== 0) return 0;
    return storedTotal;
  }

  const regularH = Math.max(0, totalH - otH);
  const rate = Number(log?.hourly_rate);

  // OLD: member hourly first; service line (fallback) only when member rate was 0.
  // let r = Number.isFinite(rate) ? rate : 0;
  // if (r === 0 && fallbackRate > 0) {
  //   const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
  //   const isDriver = log?.isDriver === 1 || log?.isDriver === '1' || log?.is_driver === 1 || log?.is_driver === '1';
  //   const taskName = (log?.task_name || '').toString().trim().toUpperCase();
  //   const isDefaultLabor = sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver));
  //   if (isDefaultLabor) r = fallbackRate;
  // }

  let r = Number.isFinite(rate) ? rate : 0;
  const fb = Number(fallbackRate);
  if (fb > 0 && isRecipeCostInstallLaborTimelog(log)) {
    r = fb;
  } else if (r === 0 && fb > 0) {
    const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
    const isDriver = log?.isDriver === 1 || log?.isDriver === '1' || log?.is_driver === 1 || log?.is_driver === '1';
    const taskName = (log?.task_name || '').toString().trim().toUpperCase();
    const isDefaultLabor = sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver));
    if (isDefaultLabor) r = fb;
  }

  return regularH * r + otH * r * 1.5;
}

export function getLaborTimelogBill(log, fallbackBillRate) {
  const isBillable =
    log?.billable === 1 ||
    log?.billable === '1' ||
    log?.billable === true ||
    log?.is_billable === 1 ||
    log?.is_billable === '1' ||
    log?.is_billable === true;
  if (!isBillable) return 0;

  const direct = Number(log?.billable_amount);
  if (Number.isFinite(direct)) return direct;

  const totalH = Number(log?.hours || log?.total_hours) || 0;
  const otH = Number(log?.ot_hours || log?.total_ot_hours) || 0;
  const regularH = Math.max(0, totalH - otH);
  const billRate = Number(log?.bill_rate);

  // OLD: bill used member bill_rate first; service fallback only when bill was 0.
  // let br = Number.isFinite(billRate) ? billRate : 0;
  // if (br === 0 && fallbackBillRate > 0) { ... isDefaultLabor ... br = fallbackBillRate; }

  let br = Number.isFinite(billRate) ? billRate : 0;
  const fbb = Number(fallbackBillRate);
  if (fbb > 0 && isRecipeCostInstallLaborTimelog(log)) {
    br = fbb;
  } else if (br === 0 && fbb > 0) {
    const sId = Number(log?.service_id || log?.rs_id || log?.customer_order_product_id || 0);
    const isDriver = log?.isDriver === 1 || log?.isDriver === '1' || log?.is_driver === 1 || log?.is_driver === '1';
    const taskName = (log?.task_name || '').toString().trim().toUpperCase();
    const isDefaultLabor = sId === 10 || (sId === 0 && (taskName === 'INSTALL LABOR' || taskName === 'LABOR INSTALL' || isDriver));
    if (isDefaultLabor) br = fbb;
  }

  const hasLaborHours = regularH + otH > 0;
  return hasLaborHours ? regularH * br + otH * br * 1.5 : 0;
}

/** Non-T&M actual rows: only cost-card entry or scan (taken / jobcost qty / jobcost cost). T&M keeps legacy loose rules. */
export function includeJobCostServiceRowActual(service, isTM = false) {
  if (!service) return false;
  if (isTM) {
    if (service.taken === 1 || service.taken === '1') return true;
    if (parseFloat(service.jobcost_quantity || 0) > 0) return true;
    if (parseFloat(service.jobcost_total_cost || service.total_cost || 0) > 0) return true;
    if (parseFloat(service.qty || 0) > 0) return true;
    if (parseFloat(service.total_bill || 0) > 0) return true;
    if (parseFloat(service.jobcost_total_bill || 0) > 0) return true;
    return false;
  }
  if (service.taken === 1 || service.taken === '1') return true;
  if (parseFloat(service.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(service.jobcost_total_cost || 0) > 0) return true;
  return false;
}

/** Non-T&M actual materials: taken or job-cost qty/total only. */
export function includeJobCostMaterialRowActual(material, isTM = false) {
  if (!material) return false;
  if (isTM) {
    if (material.taken === 1 || material.taken === '1') return true;
    if (parseFloat(material.jobcost_quantity || 0) > 0) return true;
    if (parseFloat(material.jobcost_total || 0) > 0) return true;
    if (parseFloat(material.quantity || 0) > 0) return true;
    if (parseFloat(material.total_bill || 0) > 0) return true;
    if (parseFloat(material.jobcost_total_bill || 0) > 0) return true;
    return false;
  }
  if (material.taken === 1 || material.taken === '1') return true;
  if (parseFloat(material.jobcost_quantity || 0) > 0) return true;
  if (parseFloat(material.jobcost_total || 0) > 0) return true;
  return false;
}

/**
 * Production labor total for compare/summary.
 * Non-T&M: Labor tab cost is authoritative when present (matches Expense → Labor badge).
 * Recipe-service + payout stacking double-counts (~$864 + $467 → $1331).
 */
export function resolveEffectiveProductionLabor({
  recipeLabor = 0,
  timelogLabor = 0,
  tabLabor = 0,
  isTM = false,
} = {}) {
  const recipe = Number(recipeLabor || 0);
  const timelog = Number(timelogLabor || 0);
  const tab = Number(tabLabor || 0);
  if (isTM) {
    // T&M: keep legacy max-of-all-sources (do not change existing T&M behaviour).
    return Math.max(recipe, timelog, tab);
  }
  // Backup before production labor tab-priority fix:
  // if (tab > 0 || timelog > 0) {
  //   return Math.max(tab, timelog);
  // }
  if (tab > 0) return tab;
  if (timelog > 0) return timelog;
  return recipe;
}

/** Service line actual cost for compare/actual totals — non-T&M ignores formula total_cost until job-cost entry. */
export function getJobCostServiceActualCost(service, isTM = false) {
  if (!service) return 0;
  if (isTM) {
    return parseFloat(service.jobcost_total_cost) || parseFloat(service.total_cost) || 0;
  }
  if (!includeJobCostServiceRowActual(service, false)) return 0;
  return parseFloat(service.jobcost_total_cost || 0);
}

export function getRecipeCostLaborRowComputed(log, fallbackRate, fallbackBillRate) {
  const totalH = Number(log?.hours || log?.total_hours) || 0;
  const otH = Number(log?.ot_hours || log?.total_ot_hours) || 0;
  const regularH = Math.max(0, totalH - otH);
  const costRate = Number(log?.hourly_rate);
  const billRate = Number(log?.bill_rate);

  const fb = Number(fallbackRate);
  const fbb = Number(fallbackBillRate);
  const useServiceCost = fb > 0 && isRecipeCostInstallLaborTimelog(log);
  const useServiceBill = fbb > 0 && isRecipeCostInstallLaborTimelog(log);
  // OLD: const safeCostRate = (Number.isFinite(costRate) && costRate > 0) ? costRate : (fallbackRate || 0);
  // OLD: const safeBillRate = (Number.isFinite(billRate) && billRate > 0) ? billRate : (fallbackBillRate || 0);
  const safeCostRate = useServiceCost
    ? fb
    : (Number.isFinite(costRate) && costRate > 0 ? costRate : fb || 0);
  const safeBillRate = useServiceBill
    ? fbb
    : (Number.isFinite(billRate) && billRate > 0 ? billRate : fbb || 0);
  
  const hasLaborHours = regularH + otH > 0;
  return {
    regularH,
    otH,
    costRate: safeCostRate,
    displayBillRate: hasLaborHours ? safeBillRate : 0,
    cost: getLaborTimelogCost(log, fallbackRate),
    bill: getLaborTimelogBill(log, fallbackBillRate),
    hasLaborHours
  };
}
